import { useState } from "react";
import { opApi, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, Eye, Send, Loader2, ChevronLeft, CheckCircle2, XCircle } from "lucide-react";

function StatusPill({ status }) {
  if (status === "sent") return <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Sent</span>;
  if (status === "logged") return <span className="inline-flex items-center gap-1 text-xs text-amber-700"><Mail className="h-3.5 w-3.5" /> Test</span>;
  return <span className="inline-flex items-center gap-1 text-xs text-destructive"><XCircle className="h-3.5 w-3.5" /> Failed</span>;
}

export function CustomerEmailsCell({ customerId, customerName }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [resending, setResending] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await opApi.get(`/tenant/customers/${customerId}/emails`); setRows(r.data || []); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const view = async (id) => {
    setPreview({ loading: true });
    try { const r = await opApi.get(`/tenant/email-log/${id}`); setPreview(r.data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setPreview(null); }
  };
  const resend = async (id) => {
    setResending(id);
    try { const r = await opApi.post(`/tenant/email-log/${id}/resend`); toast.success(r.data.message || "Re-sent."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setResending(null); }
  };
  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <>
      <button className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors"
        title="Email history" onClick={() => { setOpen(true); setPreview(null); load(); }}
        data-testid={`customer-emails-icon-${customerId}`}>
        <Mail className="h-4 w-4" />
      </button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPreview(null); }}>
        <DialogContent className="max-w-2xl p-0 gap-0" data-testid={`customer-emails-dialog-${customerId}`}>
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
            <DialogTitle className="text-base flex items-center gap-2">
              {preview && (
                <button onClick={() => setPreview(null)} className="text-muted-foreground hover:text-foreground" data-testid="customer-emails-back">
                  <ChevronLeft className="h-4 w-4" />
                </button>
              )}
              {preview ? (preview.subject || "Loading…") : `Emails to ${customerName || "this customer"}`}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {preview ? preview.to_address : "Everything MorrCart has sent to this customer."}
            </DialogDescription>
          </DialogHeader>

          {preview ? (
            <div className="bg-[#f5f5f5] max-h-[70vh] overflow-auto">
              {preview.html && (
                <iframe title="customer-email-preview" srcDoc={preview.html}
                  className="w-full border-0" style={{ height: "60vh" }} sandbox=""
                  data-testid="customer-email-preview-frame" />
              )}
              {preview.loading && <div className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-auto">
              {loading ? (
                <div className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
              ) : rows.length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground" data-testid="customer-emails-empty">No emails sent to this customer yet.</div>
              ) : (
                <div className="divide-y divide-border">
                  {rows.map((r) => (
                    <div key={r.id} className="flex items-center gap-3 px-6 py-3" data-testid={`customer-email-row-${r.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{r.subject}</div>
                        <div className="text-xs text-muted-foreground truncate">{fmt(r.created_at)}{r.resend_of ? " · re-sent" : ""}</div>
                      </div>
                      <StatusPill status={r.status} />
                      <Button variant="outline" size="sm" className="rounded-none gap-1.5 shrink-0" onClick={() => view(r.id)} data-testid={`customer-email-view-${r.id}`}>
                        <Eye className="h-3.5 w-3.5" /> View
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-none gap-1.5 shrink-0"
                        disabled={!r.resendable || resending === r.id}
                        title={r.resendable ? "Re-send" : "Contains a one-time secure link — can't be re-sent"}
                        onClick={() => resend(r.id)} data-testid={`customer-email-resend-${r.id}`}>
                        {resending === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Resend
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
